import { useEffect, useRef } from 'react'
import { Archive, Download, Loader2, Mail, X } from 'lucide-react'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { useIsMobile } from '../ui/use-mobile'
import { cn } from '@/lib/utils'

type Props = {
  selectedIds: string[]
  isExporting: boolean
  onExport: () => void
  onClear: () => void
  // Story 3.2 : `onRelaunch` optionnel pour rétrocompat défensive. Si non
  // fourni, le bouton reste disabled (cas : consommateur pré-3.2 qui ne
  // branche pas encore la modale).
  onRelaunch?: (selectedIds: string[]) => void
}

export function BulkActions({ selectedIds, isExporting, onExport, onClear, onRelaunch }: Props) {
  const isMobile = useIsMobile()
  const toolbarRef = useRef<HTMLDivElement>(null)
  const count = selectedIds.length

  // Focus initial + focus trap clavier + Escape.
  // Note : toolbar non-modale → on laisse la souris interagir avec la liste,
  // le trap sert uniquement au cycle Tab ↔ Shift+Tab dans la toolbar.
  useEffect(() => {
    if (count === 0) return
    // Mémorise l'élément focalisé avant l'ouverture pour le restaurer à la
    // fermeture — conformité AC #7 (spec Story 2.4).
    const previouslyFocused = document.activeElement as HTMLElement | null
    const exportBtn = toolbarRef.current?.querySelector<HTMLButtonElement>(
      '[data-bulk-action="export"]'
    )
    exportBtn?.focus()

    const isEditableTarget = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      return el.isContentEditable
    }

    const onKeyDown = (e: KeyboardEvent) => {
      const toolbar = toolbarRef.current
      if (e.key === 'Escape') {
        // Ne jamais intercepter Escape si l'utilisateur édite un champ
        // (search input, textarea, contenteditable) — l'Escape natif doit
        // pouvoir annuler la saisie. Limite aussi au contexte où le focus
        // est dans la toolbar ou neutre (body), pour ne pas hijacker la
        // touche Escape au reste de l'app (modals, dropdowns).
        if (isEditableTarget(e.target)) return
        const active = document.activeElement
        if (active && active !== document.body && !toolbar?.contains(active)) return
        e.preventDefault()
        onClear()
        return
      }
      if (e.key !== 'Tab') return
      // Le focus trap Tab ne s'active que si le focus est DÉJÀ dans la toolbar.
      if (!toolbar?.contains(document.activeElement)) return
      const focusables = toolbar.querySelectorAll<HTMLElement>(
        'button:not([disabled]):not([tabindex="-1"])'
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Restaure le focus précédent (AC #7) s'il est toujours attaché au DOM.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus()
      }
    }
  }, [count, onClear])

  if (count === 0) return null

  return (
    <TooltipProvider>
      <div
        ref={toolbarRef}
        role="toolbar"
        aria-label="Actions groupées sur la sélection"
        className={cn(
          'z-30 flex items-center gap-2 bg-background/95 backdrop-blur shadow-sm',
          isMobile
            ? 'sticky bottom-0 z-40 border-t px-3 py-2 shadow-lg'
            : 'sticky top-0 border-b px-3 py-2 rounded-t-lg'
        )}
      >
        <span
          aria-live="polite"
          className="text-sm font-medium"
        >
          {count} sélectionné{count > 1 ? 's' : ''}
        </span>

        <div className="flex-1" />

        <Button
          size="sm"
          variant="default"
          data-bulk-action="export"
          onClick={onExport}
          disabled={isExporting}
          aria-label="Exporter la sélection au format CSV"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-4 w-4" aria-hidden="true" />
          )}
          <span>Exporter</span>
        </Button>

        {!isMobile && (
          <>
            {onRelaunch ? (
              <Button
                size="sm"
                variant="outline"
                data-bulk-action="relaunch"
                onClick={() => onRelaunch(selectedIds)}
                disabled={isExporting}
                aria-label="Relancer la sélection"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                Relancer
              </Button>
            ) : (
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      size="sm"
                      variant="outline"
                      data-bulk-action="relaunch"
                      disabled
                      aria-disabled="true"
                      tabIndex={-1}
                      className="opacity-50 cursor-not-allowed"
                    >
                      <Mail className="h-4 w-4" aria-hidden="true" />
                      Relancer
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Relance indisponible dans ce contexte</TooltipContent>
              </Tooltip>
            )}

            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    data-bulk-action="archive"
                    disabled
                    aria-disabled="true"
                    tabIndex={-1}
                    className="opacity-50 cursor-not-allowed"
                  >
                    <Archive className="h-4 w-4" aria-hidden="true" />
                    Archiver
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                Disponible dans Epic 3 — gestion lifecycle fournisseur
              </TooltipContent>
            </Tooltip>
          </>
        )}

        {isMobile && onRelaunch && (
          <Button
            size="sm"
            variant="outline"
            data-bulk-action="relaunch"
            onClick={() => onRelaunch(selectedIds)}
            disabled={isExporting}
            aria-label="Relancer la sélection"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            Relancer
          </Button>
        )}

        <Button
          size="sm"
          variant="ghost"
          data-bulk-action="clear"
          onClick={onClear}
          disabled={isExporting}
          aria-label="Annuler la sélection"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </TooltipProvider>
  )
}
